import { CATALOG_RESOLVER_SELECT, enrichCatalogVariantResolvers } from '../lib/catalogVariantResolver'
import { loadCatalogSearchParts, catalogAvailabilityMessage } from '../lib/catalogSearchAvailability'
import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { supabase } from '../lib/supabase'
import { groupCatalogResults } from '../lib/catalogSearchResults'
import { resolvedConcentrationDisplay } from '../lib/concentrationResolver'
import { applySoftHitPolicy, catalogSearchDecision } from '../lib/catalogSearchConfidence'
import {
  applyBareNameCollisionPolicy,
  applyCanonicalIntentLines,
  applyCanonicalIntentPolicy,
  expandSearchQuery,
  rerankCatalogResults,
} from '../lib/searchQueryAliases'
import { createSearchId, recordSearchTelemetry } from '../lib/searchTelemetry'
import { ruleRank, secondSearchQuery } from '../lib/catalogRuleRank'
import {
  guardNeedsWiderPage,
  promoteStrictMatches,
  recoveryPlan,
  routePrimarySearch,
  selectRankedRows,
  shippedNameMatch,
  shouldSearchQuery,
  v2RecoveryAllowed,
} from '../lib/collectionEntryMatch'

function logCatalogDemand(event) {
  void (async () => {
    try {
      const { data: { user } = {} } = await supabase.auth.getUser()
      await supabase.from('catalog_demand_log').insert({
        ...event,
        user_id: user?.id || null,
      })
    } catch {
      // Demand logging is best-effort. Search and add flows must keep working
      // before the gated migration exists or if logging is blocked by RLS.
    }
  })()
}

// Network-level failures must never reach the founder as a raw stack message.
const NETWORK_ERROR_RE = /load failed|failed to fetch|network|aborted/i
function friendlySearchError(e) {
  const msg = e?.message || ''
  return NETWORK_ERROR_RE.test(msg)
    ? 'Search couldn’t reach the catalog — check your connection and try again.'
    : msg || 'Search failed — please try again.'
}

// ── Catalog search hook ───────────────────────────────────────────────────────
// Debounced fuzzy search against the canonical fragrance.catalog table via the
// search_catalog RPC. Powers the tap-to-add path in the Add flow.
//
//   const { query, setQuery, results, loading, error } = useCatalogSearch()
//
// Results are lightweight rows (id, name, house, concentration, year,
// scent_family, card_image, fragrantica_rating, collection_ready). Fetch the
// full row with fetchCatalogRow(id) when the user selects one.
// `limit` must stay <= the search_catalog ceiling (400) or the RPC silently
// truncates. At 60 a house lineup was unreachable: "azzaro" returns cross-house
// trigram matches too, so only 44 of Azzaro's 69 rows fit in 60 slots. 200 covers
// every house except the seven with >200 rows (Guerlain, Arabian Oud, Alexandria,
// Dior, Lattafa, Oakcha, YSL, Dua) — those need paging, not a bigger payload.
export function useCatalogSearch({ debounceMs = 250, limit = 200 } = {}) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [matchConfidence, setMatchConfidence] = useState(0)
  const [matchReason, setMatchReason] = useState('not_searched')
  const [fallbackReason, setFallbackReason] = useState(null)
  const [searchId, setSearchId] = useState(null)
  const [lines, setLines] = useState([])
  const timer = useRef(null)
  const demandLogTimer = useRef(null)
  const reqId = useRef(0)
  const completedSearch = useRef(null)

  const recordAbandonment = useCallback(() => {
    const prior = completedSearch.current
    if (!prior || prior.selected || prior.abandonmentLogged) return
    prior.abandonmentLogged = true
    recordSearchTelemetry({
      event_type: 'search_abandoned',
      search_id: prior.searchId,
      query: prior.query,
      result_count: prior.resultCount,
      match_confidence: prior.matchConfidence,
      fallback_reason: prior.fallbackReason,
    })
  }, [])

  useEffect(() => {
    const q = query.trim()
    if (completedSearch.current?.query !== q) recordAbandonment()
    // Invalidate an in-flight response as soon as the text changes. Previously
    // this counter advanced only after the next debounce fired, leaving a race
    // where stale results could overwrite the founder's final query.
    const myReq = ++reqId.current
    clearTimeout(timer.current)
    clearTimeout(demandLogTimer.current)

    // Min 3 chars (founder ruling 2026-07-26): 2-char queries cost 4.5-7.5s server-side.
    // Y, W, M7, Sì, and Pi are whole names in the catalog. They search as
    // the full name, not as a one- or two-character trigram scan.
    if (!shouldSearchQuery(q)) {
      setResults([]); setLines([]); setLoading(false); setError(null)
      setMatchConfidence(0); setMatchReason('not_searched'); setFallbackReason(null); setSearchId(null)
      return
    }

    setLoading(true)
    timer.current = setTimeout(async () => {
      const activeSearchId = createSearchId()
      const startedAt = performance.now()
      setSearchId(activeSearchId)
      const routed = routePrimarySearch(q)
      const rpcQuery = routed.rpc === 'v2' ? routed.term : expandSearchQuery(routed.term)
      const attempt = async (term = rpcQuery, rpc = 'findability', lim = limit) => {
        if (rpc === 'v2') {
          const response = await supabase.rpc('search_catalog_v2', { q: term, lim })
          if (response.error) throw response.error
          return response.data
        }
        let response = await supabase.rpc('search_catalog_findability_v1', { q: term, lim })
        if (response.error?.code === 'PGRST202' || response.error?.code === '42883') {
          response = await supabase.rpc('search_catalog_v2', { q: term, lim })
        }
        // Keep search usable during the separately gated DDL rollout. Legacy
        // rows deliberately present as unknown until resolver evidence exists.
        if (response.error?.code === 'PGRST202' || response.error?.code === '42883') {
          response = await supabase.rpc('search_catalog', { q: term, lim })
        }
        const { data, error: err } = response
        if (err) throw err
        return data
      }
      const fetchLines = async () => {
        const { data, error: lineErr } = await supabase.rpc('fos_search_lines_v1', { q: rpcQuery, lim: 120 })
        if (lineErr) throw lineErr
        return data || []
      }
      // FOS-SEARCH-RULE-LAYER-001: when collector shorthand or concentration
      // wording rewrites the query ("bdc" -> "bleu de chanel", "sauvage edp"
      // -> "sauvage"), search the rewrite in parallel and merge. Best effort:
      // a failed second search never blocks the typed one.
      const secondQuery = secondSearchQuery(q)
      const secondRpcQuery = secondQuery ? expandSearchQuery(secondQuery) : null
      const secondRowsPromise = secondRpcQuery ? attempt(secondRpcQuery).catch(() => []) : Promise.resolve([])
      try {
        const parts = await loadCatalogSearchParts({ loadRows: () => attempt(rpcQuery, routed.rpc), loadLines: fetchLines, isCurrent: () => myReq === reqId.current })
        const data = parts.rows
        const lineRows = parts.lines
        if (myReq !== reqId.current) return
        const postProcess = (text, rpcText, raw) => applySoftHitPolicy(
          rpcText,
          applyCanonicalIntentPolicy(
            text,
            applyBareNameCollisionPolicy(text, rerankCatalogResults(text, raw || [])),
          ),
        )
        const primaryRows = postProcess(q, rpcQuery, data)
        const secondRows = secondQuery ? postProcess(secondQuery, secondRpcQuery, await secondRowsPromise) : []
        if (myReq !== reqId.current) return
        const recoveryBatches = []
        const rankedNow = ruleRank(q, primaryRows, secondRows)
        // Recovery runs only when this page has no shipped identity match.
        // A recovered row is kept only when it matches.
        if (!rankedNow.some(row => shippedNameMatch(q, row))) {
          for (const step of recoveryPlan(q)) {
            if (step.rpc === 'v2' && !v2RecoveryAllowed(q, expandSearchQuery(q))) continue
            if (myReq !== reqId.current) return
            let recovered = []
            try {
              recovered = await attempt(step.term, step.rpc)
            } catch {
              continue
            }
            if (myReq !== reqId.current) return
            recoveryBatches.push({ rpc: step.rpc, term: step.term, rows: postProcess(q, step.term, recovered) })
            const pooled = [...rankedNow, ...recoveryBatches.flatMap(batch => batch.rows || [])]
            if (pooled.some(row => shippedNameMatch(q, row))) break
          }
        }
        // A short page can omit a bottle the wider page returns ("Vetiver
        // and Orange"). Keep the first page as the shipped order, and ask
        // for 200 only when that top is not already the typed bottle.
        let wideRows = null
        if (limit < 200 && guardNeedsWiderPage(q, primaryRows, secondRows, recoveryBatches, expandSearchQuery(q))) {
          try {
            const widePrimary = await attempt(rpcQuery, routed.rpc, 200)
            if (myReq !== reqId.current) return
            const wideSecond = secondRpcQuery ? await attempt(secondRpcQuery, 'findability', 200) : []
            if (myReq !== reqId.current) return
            wideRows = {
              primary: postProcess(q, rpcQuery, widePrimary),
              second: secondQuery ? postProcess(secondQuery, secondRpcQuery, wideSecond) : [],
            }
          } catch {
            // The first page still ranks when the wider lookup fails.
          }
        }
        const rows = selectRankedRows(q, primaryRows, secondRows, recoveryBatches, expandSearchQuery(q), wideRows)
        const decisionPrimaryRows = wideRows?.primary?.length ? wideRows.primary : primaryRows
        const decisionSecondRows = Array.isArray(wideRows?.second) ? wideRows.second : secondRows
        const primaryDecision = catalogSearchDecision(rpcQuery, decisionPrimaryRows)
        const secondDecision = decisionSecondRows.length ? catalogSearchDecision(secondRpcQuery, decisionSecondRows) : null
        const decision = secondDecision && secondDecision.confidence > primaryDecision.confidence
          ? secondDecision
          : (primaryRows.length || !secondDecision ? primaryDecision : secondDecision)
        const latencyMs = performance.now() - startedAt
        setResults(rows)
        const visibleLines = applyCanonicalIntentLines(q, lineRows)
        setLines(visibleLines)
        setMatchConfidence(decision.confidence)
        setMatchReason(decision.reason)
        setFallbackReason(decision.fallbackReason)
        const availabilityError = catalogAvailabilityMessage({ ...parts, rows, lines: visibleLines })
        setError(availabilityError)
        if (availabilityError) {
          setFallbackReason(null)
          setMatchReason('search_failed')
          completedSearch.current = null
          recordSearchTelemetry({ event_type: 'search_failed', search_id: activeSearchId, query: q, latency_ms: latencyMs, error_code: parts.rowError?.code || parts.lineError?.code || 'search_error' })
          return
        }
        completedSearch.current = {
          searchId: activeSearchId,
          query: q,
          resultCount: rows.length,
          matchConfidence: decision.confidence,
          fallbackReason: decision.fallbackReason,
          selected: false,
          abandonmentLogged: false,
        }
        recordSearchTelemetry({
          event_type: 'search_completed',
          search_id: activeSearchId,
          query: q,
          result_count: rows.length,
          latency_ms: latencyMs,
          match_confidence: decision.confidence,
          fallback_reason: decision.fallbackReason,
          metadata: {
            match_reason: decision.reason,
            expanded_query: rpcQuery !== q ? rpcQuery : undefined,
            rule_second_query: secondRpcQuery || undefined,
          },
        })
        demandLogTimer.current = setTimeout(() => {
          logCatalogDemand({
            event_type: 'search',
            query: q,
            result_count: rows.length,
          })
        }, 1500)
      } catch (e) {
        if (myReq !== reqId.current) return
        setError(friendlySearchError(e))
        setResults([])
        setLines([])
        setMatchConfidence(0)
        setMatchReason('search_failed')
        setFallbackReason(null)
        recordSearchTelemetry({
          event_type: 'search_failed',
          search_id: activeSearchId,
          query: q,
          latency_ms: performance.now() - startedAt,
          error_code: e?.code || e?.name || 'search_error',
        })
      } finally {
        if (myReq === reqId.current) setLoading(false)
      }
    }, debounceMs)

    return () => clearTimeout(timer.current)
  }, [query, debounceMs, limit, recordAbandonment])

  useEffect(() => () => {
    recordAbandonment()
    clearTimeout(timer.current)
    clearTimeout(demandLogTimer.current)
  }, [recordAbandonment])

  const reset = useCallback(() => {
    recordAbandonment()
    reqId.current += 1
    clearTimeout(timer.current)
    clearTimeout(demandLogTimer.current)
    setQuery('')
    setResults([])
    setLines([])
    setError(null)
    setMatchConfidence(0)
    setMatchReason('not_searched')
    setFallbackReason(null)
    setSearchId(null)
    setLoading(false)
    completedSearch.current = null
  }, [recordAbandonment])

  const markSelected = useCallback((row) => {
    const active = completedSearch.current
    if (!active || active.selected) return
    active.selected = true
    recordSearchTelemetry({
      event_type: 'result_selected',
      search_id: active.searchId,
      query: active.query,
      result_count: active.resultCount,
      match_confidence: active.matchConfidence,
      fallback_reason: active.fallbackReason,
      selected_catalog_id: row?.id || null,
    })
  }, [])

  const groups = useMemo(() => groupCatalogResults(results, query), [results, query])

  const loadLineVariants = useCallback(async (house, lineKey) => {
    try {
      const { data, error } = await supabase.rpc('fos_search_line_variants_v1', {
        p_house: house,
        p_line_key: lineKey,
        lim: 80,
      })
      if (error) return []
      const variants = await enrichCatalogVariantResolvers(data || [], ids =>
        supabase.from('catalog').select(CATALOG_RESOLVER_SELECT).in('id', ids),
      )
      return promoteStrictMatches(query, ruleRank(query, applyCanonicalIntentPolicy(
        query,
        applyBareNameCollisionPolicy(query, rerankCatalogResults(query, variants)),
      )))
    } catch {
      return []
    }
  }, [query])

  return {
    query,
    setQuery,
    results,
    lines,
    loadLineVariants,
    groups,
    loading,
    error,
    reset,
    searchId,
    matchConfidence,
    matchReason,
    fallbackReason,
    shouldDiscover: !loading && !error && query.trim().length >= 3 && Boolean(fallbackReason),
    markSelected,
  }
}

// Fetch the full canonical row for a selected catalog id.
export async function fetchCatalogRow(id) {
  const [catalogResult, suppressionResult] = await Promise.all([
    supabase.from('catalog').select('*').eq('id', id).single(),
    supabase.from('image_qa_suppress').select('catalog_id').eq('catalog_id', id).maybeSingle(),
  ])
  const { data, error } = catalogResult
  if (error) throw error
  logCatalogDemand({ event_type: 'select', frag_id: id })

  // Fail closed for imagery. A suppression lookup should be readable to an
  // authenticated app user; if that lookup ever loses access, the product
  // details still load but its image remains blank rather than leaking an
  // already-adjudicated unsafe asset.
  const imageSuppressed = suppressionResult.error || suppressionResult.data
  return imageSuppressed
    ? { ...data, card_image: null, image_flagged: true }
    : data
}

// Fetch all concentration variants for a given house + name.
// Returns an array of lightweight catalog rows — one per concentration.
// Used by the Add flow to show a concentration picker when multiple variants exist.
export async function fetchConcentrationVariants(house, name) {
  let response = await supabase.rpc('get_concentration_variants_v2', {
    p_house: house,
    p_name: name,
  })
  if (response.error?.code === 'PGRST202' || response.error?.code === '42883') {
    response = await supabase.rpc('get_concentration_variants', {
      p_house: house,
      p_name: name,
    })
  }
  const { data, error } = response
  if (error) throw error
  return data || []
}

// Map a catalog row → the QuickAddModal `data` shape (user supplies size etc.).
// ARCHITECTURE: catalog_id is the canonical KB reference — it MUST be preserved here.
// Dropping it severs the collection row from the catalog and violates Invariant #2.
export function catalogRowToFormData(c) {
  return {
    catalog_id:         c.id || null,    // ← canonical KB reference (Workstream D fix, 2026-06-24)
    name:               c.name || '',
    house:              c.house || '',
    concentration:      resolvedConcentrationDisplay(c),
    bottle_ml:          '',
    year:               c.year ?? '',
    release_season:     c.release_season || '',
    top_notes:          c.top_notes || [],
    mid_notes:          c.mid_notes || [],
    base_notes:         c.base_notes || [],
    seasons:            c.seasons || [],
    occasions:          c.occasions || [],
    layering_role:      c.layering_role || '',
    projection:         c.projection || '',
    fragrantica_url:    c.fragrantica_url || '',
    fragrantica_rating: c.fragrantica_rating ?? '',
    scent_family:       c.scent_family || '',
    gender_class:       c.gender_class || '',
    card_image:         c.card_image || '',
    inspired_by:        c.inspired_by || '',
    user_notes:         '',
  }
}
